import * as React from 'react';
import { Mail, Lock, Shield } from 'lucide-react';
import apiService from '../../services/api';
import {
  signInWithFirebaseEmail,
  signInWithMicrosoft,
  extractFirebaseUserInfo,
  type FirebaseUserInfo,
} from '../../services/firebaseAuth';
import { getSamlProvider, isFirebaseInitialized } from '../../config/firebase';
import './Login.css';

interface LoginProps {
  onLoginSuccess: () => void;
}

function webAppLoginUrl(): string {
  const base = (process.env.COURTYARD_WEB_APP_URL || '').trim();
  if (base) {
    return `${base.replace(/\/$/, '')}/login`;
  }
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3000/login';
  }
  return 'https://thecourtyard.ai/login';
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isFirebaseReady = isFirebaseInitialized();
  const hasMicrosoftSso = Boolean(getSamlProvider());

  const exchangeFirebaseForBackend = React.useCallback(
    async (
      userInfo: FirebaseUserInfo,
      idToken: string,
      provider: string,
      authMethod: string
    ): Promise<boolean> => {
      const token = await apiService.firebaseLogin(
        idToken,
        userInfo.email,
        userInfo.name,
        userInfo.uid,
        userInfo.emailVerified,
        provider,
        authMethod
      );
      if (token) {
        console.log('[Courtyard][AuthTrace] [Login] success, calling onLoginSuccess', {
          tokenLen: token.length,
        });
        onLoginSuccess();
        return true;
      }
      setError('Login failed. Please check your credentials.');
      return false;
    },
    [onLoginSuccess]
  );

  const handleMicrosoftClick = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!isFirebaseReady) {
        setError('Firebase is not configured. Please check your Firebase settings.');
        return;
      }
      const result = await signInWithMicrosoft();
      if (!result.success || !result.idToken) {
        setError(result.error || 'Microsoft sign-in failed');
        return;
      }
      const userInfo = result.dialogUser
        ? result.dialogUser
        : result.user
          ? extractFirebaseUserInfo(result.user)
          : null;
      if (!userInfo) {
        setError('Microsoft sign-in failed');
        return;
      }
      await exchangeFirebaseForBackend(userInfo, result.idToken, 'microsoft.com', 'gcip_federation');
    } catch (err: unknown) {
      console.error('Microsoft login error:', err);
      let detail: string | undefined;
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const d = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
        detail = typeof d === 'string' ? d : undefined;
      }
      setError(detail ?? (err instanceof Error ? err.message : 'Sign-in failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!isFirebaseReady) {
        setError('Firebase is not configured. Please check your Firebase settings.');
        return;
      }

      const firebaseResult = await signInWithFirebaseEmail(email, password);
      if (!firebaseResult.success || !firebaseResult.user || !firebaseResult.idToken) {
        setError(firebaseResult.error || 'Firebase authentication failed');
        return;
      }

      await exchangeFirebaseForBackend(
        extractFirebaseUserInfo(firebaseResult.user),
        firebaseResult.idToken,
        'password',
        'firebase'
      );
    } catch (err: unknown) {
      console.error('Login error:', err);
      let detail: string | undefined;
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const d = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
        detail = typeof d === 'string' ? d : undefined;
      }
      setError(
        detail ?? (err instanceof Error ? err.message : 'Login failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-header">
        <div className="login-logo-wrap">
          <span className="login-logo" aria-hidden>
            <span className="login-logo-c">C</span>
            <span className="login-logo-rest">
              <span className="login-logo-bold">ourt</span>
              <span className="login-logo-light">yard</span>
              <span className="login-logo-bold">.ai</span>
            </span>
          </span>
        </div>
        <h1 className="login-welcome">Welcome</h1>
        <p className="login-lead">Sign in to your courtyard.ai account</p>
      </div>

      <div className="login-card-wrap">
        <div className="login-card">
          <div className="login-card-inner">
            <div className="login-sso-block">
              <div className="login-sso-label">
                <Shield className="login-sso-shield" aria-hidden />
                <span>Secure Microsoft SSO Authentication</span>
              </div>
              <button
                type="button"
                className="login-ms-button"
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  void handleMicrosoftClick();
                }}
                disabled={loading || !isFirebaseReady}
                title={!hasMicrosoftSso ? 'Set FIREBASE_SAML_PROVIDER_ID to enable Microsoft sign-in' : undefined}
              >
                <span className="login-ms-logo" aria-hidden>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="9" height="9" fill="#F25022" />
                    <rect x="11" width="9" height="9" fill="#7FBA00" />
                    <rect y="11" width="9" height="9" fill="#00A4EF" />
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                </span>
                <span>{loading ? 'Redirecting to Microsoft...' : 'Sign in with Microsoft'}</span>
              </button>
              <p className="login-sso-foot">You will be redirected to Microsoft for secure authentication</p>
            </div>

            <div className="login-divider">
              <span className="login-divider-line" />
              <span className="login-divider-text">Or continue with email</span>
              <span className="login-divider-line" />
            </div>

            {error && <div className="login-error">{error}</div>}

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="login-field">
                <label htmlFor="email" className="login-label">
                  Email address
                </label>
                <div className="login-input-wrap">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                    autoComplete="email"
                    className="login-input"
                  />
                  <Mail className="login-input-icon" aria-hidden />
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="password" className="login-label">
                  Password
                </label>
                <div className="login-input-wrap">
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                    className="login-input"
                  />
                  <Lock className="login-input-icon" aria-hidden />
                </div>
              </div>

              <button type="submit" className="login-email-submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign in with Email'}
              </button>

              <div className="login-signup-row">
                <a
                  className="login-signup-link"
                  href={webAppLoginUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Don&apos;t have an account? <span className="login-signup-accent">Sign up</span>
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
